/**
 * Pure helper used by the BMA360D drag-release flow to decide what should happen
 * when the user drops the mic on the canvas. Kept side-effect-free so the
 * caller (a React event handler) can drive UI (toasts, revert, dispatch) from
 * its outcome.
 */
export interface TileCenter {
  x: number;
  y: number;
  blocked?: boolean;
}

export interface TileSize {
  x: number;
  y: number;
}

export type TileLandingOutcome =
  | { outcome: 'no_tiles' }
  | { outcome: 'all_blocked' }
  | { outcome: 'dropped_on_blocked'; snappedLocation: { x: number; y: number } }
  | { outcome: 'dropped_on_occupied'; snappedLocation: { x: number; y: number } }
  | { outcome: 'silent'; snappedLocation: { x: number; y: number }; distanceTiles: number }
  | { outcome: 'snapped_far'; snappedLocation: { x: number; y: number }; distanceTiles: number };

export interface TileLandingOptions {
  /**
   * BMA360D rule (PIG-389): the BMA occupies a specific tile, so if the tile it is
   * dropped ON is blocked it must return to its original position rather than hop to
   * a neighbouring unblocked tile. When true, evaluation looks at the tile under the
   * drop (nearest centre regardless of blocked state) and returns `dropped_on_blocked`
   * if that tile is blocked. When false (default — TCM-X / CBC), it snaps to the
   * nearest *unblocked* tile and only reverts when every tile is blocked.
   */
  revertOnBlockedDrop?: boolean;
  /**
   * No-sharing rule: tile devices may not stack on the same (sub)tile. Pass the centre positions
   * of every OTHER tile device (mics + speakers, primary + expansion ceiling) — excluding the
   * device being dragged. When the tile under the drop coincides with one of these, evaluation
   * returns `dropped_on_occupied` so the caller reverts to the pre-drag position. Only meaningful
   * alongside revertOnBlockedDrop.
   */
  occupiedTiles?: { x: number; y: number }[];
}

/**
 * Decide whether a drop location lands silently, requires a "moved to nearest tile"
 * toast, reverts to the original position, or has no valid tile to snap to.
 *
 * - `no_tiles`: tile grid is not defined; caller should dispatch the drop location unchanged.
 * - `all_blocked`: every tile is blocked; caller should revert to the pre-drag position and toast.
 * - `dropped_on_blocked`: (BMA mode only) the tile under the drop is blocked; caller should
 *    revert to the pre-drag position and toast. Other tiles may still be free — the device is
 *    intentionally NOT relocated to them.
 * - `dropped_on_occupied`: (when `occupiedTiles` is supplied) the tile under the drop already
 *    holds another tile device; caller should revert and toast (no two devices per tile).
 * - `silent`: target tile is within one tile-width; snap normally with no toast.
 * - `snapped_far`: nearest unblocked tile is more than one tile-width away; snap and toast.
 *
 * "One tile-width" is measured against the smaller tile dimension so rectangular
 * tiles aren't penalised when the long axis exceeds 1.0.
 */
export function evaluateTileLanding(
  dropLocation: { x: number; y: number },
  tileCenters: TileCenter[] | null | undefined,
  tileSize: TileSize | null | undefined,
  options: TileLandingOptions = {},
): TileLandingOutcome {
  if (!tileCenters || tileCenters.length === 0 || !tileSize) {
    return { outcome: 'no_tiles' };
  }

  const minTileDim = Math.min(tileSize.x, tileSize.y);

  // BMA360D: evaluate the tile the device was dropped ON (nearest centre regardless of
  // blocked state). If it's blocked, revert; the BMA is never relocated to another tile.
  if (options.revertOnBlockedDrop) {
    let nearestAny: TileCenter | null = null;
    let nearestAnyDistance = Infinity;
    for (const tile of tileCenters) {
      if (typeof tile.x !== 'number' || typeof tile.y !== 'number') continue;
      const distance = Math.hypot(tile.x - dropLocation.x, tile.y - dropLocation.y);
      if (distance < nearestAnyDistance) {
        nearestAnyDistance = distance;
        nearestAny = tile;
      }
    }

    if (!nearestAny) {
      return { outcome: 'no_tiles' };
    }
    if (nearestAny.blocked) {
      return { outcome: 'dropped_on_blocked', snappedLocation: { x: nearestAny.x, y: nearestAny.y } };
    }
    // No-sharing rule: the tile under the drop already holds another tile device. Tile centres
    // and device positions are both snapped to the grid, so an exact-ish match identifies the
    // same (sub)tile. Revert rather than stack two devices on one tile.
    if (options.occupiedTiles && options.occupiedTiles.some(
      (t) => Math.abs(t.x - nearestAny!.x) < 0.01 && Math.abs(t.y - nearestAny!.y) < 0.01,
    )) {
      return { outcome: 'dropped_on_occupied', snappedLocation: { x: nearestAny.x, y: nearestAny.y } };
    }
    // PIG-525: a manual drop may land on ANY unblocked, unoccupied in-room tile — matching the
    // no-ceiling-tiles behavior. Per PIG-295 the only placement constraints are blocked tiles and
    // the room boundary (enforced by the tile grid + full_room filter), NOT the focused-coverage
    // box, which is only the reference for automatic grid placement.
    const distanceTiles = minTileDim > 0 ? nearestAnyDistance / minTileDim : 0;
    return { outcome: 'silent', snappedLocation: { x: nearestAny.x, y: nearestAny.y }, distanceTiles };
  }

  let nearest: TileCenter | null = null;
  let nearestDistance = Infinity;
  for (const tile of tileCenters) {
    if (tile.blocked) continue;
    if (typeof tile.x !== 'number' || typeof tile.y !== 'number') continue;
    const dx = tile.x - dropLocation.x;
    const dy = tile.y - dropLocation.y;
    const distance = Math.hypot(dx, dy);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = tile;
    }
  }

  if (!nearest) {
    return { outcome: 'all_blocked' };
  }

  const distanceTiles = minTileDim > 0 ? nearestDistance / minTileDim : 0;
  const snappedLocation = { x: nearest.x, y: nearest.y };

  if (distanceTiles > 1) {
    return { outcome: 'snapped_far', snappedLocation, distanceTiles };
  }
  return { outcome: 'silent', snappedLocation, distanceTiles };
}
